import React, { useEffect } from 'react';
import { Input, Select, Form, Button, InputNumber, Space, message } from 'antd';
import { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation, withTranslation } from 'react-i18next';
import constants from '../../../constants';
//import 'antd/dist/antd.css';
import './PaymentForm.scss';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import Order from '../../../Model/models/orderModel';
import OrderCreditNote from '../../../Model/creditNotes';
import OrderProduct from '../../../Model/models/orderProductModel';
import { OrderFormUtils } from '../../../utils/orderFormUtils';
import authAxios from '../../../authorization/axios';

interface Props {
  orderId: number,
  creditNoteId?: number;
  closeModal: () => Promise<void>;
}

const CreditNoteForm = ({ orderId, creditNoteId, closeModal }: Props) => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Order>();
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [count, setCount] = useState(0);

  const [form] = Form.useForm();

  const { t } = useTranslation();

  const getOrder = () => {
    authAxios.get(`${constants.api.getOrder}/${orderId}`).then(async (resultOrder) => {
      if (resultOrder.data && resultOrder.data.data) {
        const oId = resultOrder.data.data.id;
        const resultCreditNotes = await authAxios.get<{ data: OrderCreditNote[]; }>(
          constants.api.getOrderCreditNotes.replace('{orderId}', oId)
        );
        if (resultCreditNotes.data !== undefined) {
          for (let cn of resultCreditNotes.data.data) {
            const orderProducts: OrderProduct[] = resultOrder.data.data.orders_products;
            for (let cnd of cn.details || []) {
              const orderProduct = orderProducts.find(op => op.id === cnd.orderProductId);
              if (orderProduct) {
                orderProduct.quantity -= cnd.quantity;
              }
            }
          }
        }
        setOrder(resultOrder.data.data);
        setCurrencySymbol(OrderFormUtils.getCurrecy(resultOrder.data.data.currency));
        const rp = resultOrder.data.data?.orderProducts
          .filter((op: OrderProduct) => ['refunded', 'partial_refund'].includes(op.status || ''));
        if (rp.length > 0) {
          form.setFieldsValue({ products: [{ vat: 0 }] });
        }
      }
    });
  };

  useEffect(() => {
    getOrder();
    setLoading(false);
  }, []);

  const refundableProducts = order?.orderProducts.filter(
    op =>
      ['refunded', 'partial_refund'].includes(op.status || '') && op.quantity > 0
  );

  const calcProps = (name: number) => {
    const prodSelected = form.getFieldValue(['products', name, 'orderProductId']);
    const selected: any = refundableProducts?.find((item: any) => item.id === prodSelected);
    const quantity = form.getFieldValue(['products', name, 'quantity']) || 1;

    const field = form.getFieldsValue();
    const { products } = field;
    Object.assign(products[name], {
      quantity,
      vat: quantity * (selected?.vat / selected?.quantity),
      total: selected?.price * quantity
    });
    form.setFieldsValue({ products });
  };

  const handleFinish = (values: any) => {
    const paymentData: OrderCreditNote = {
      id: values.id,
      date: values.PaymentDeadline,
      creditNoteCode: values.CreditNote,
      orderId: orderId,
      details: form.getFieldValue('products')
    };
    console.log(values);
    setLoading(true);
    let promise = !creditNoteId
      ? authAxios.post(constants.api.addCreditNote, paymentData)
      : authAxios.put(constants.api.updateCreditNote, paymentData);
    promise.then((res: any) => {
      if (res.data.success) {
        setLoading(false);
        closeModal();
      }
    }).catch((err) => {
      setLoading(false);
      message.error({
        content: err.response.data.errorMessage,
        style: {
          marginTop: 100,
          zIndex: 110000
        }
      });
    });
  };

  return <Container fluid className='d-flex'>
    <div style={{ width: '100%', margin: '0 auto' }}>
      <Form
        style={{ padding: 20 }}
        form={form}
        layout='vertical'
        onFinish={handleFinish}
      >
        {refundableProducts?.length && count < refundableProducts.length && <>
          <Form.List name='products'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  // const total = test && test?.find((f: any) => f.id === form.getFieldValue('id'));
                  return (
                    <Space key={key} style={{ columnGap: 0 }} align='baseline'>
                      <Form.Item
                        {...restField}
                        name={[name, 'id']}
                        fieldKey={[key, 'id']}
                        key={`${key}-id`}
                        style={{ display: 'none' }}
                      >
                        <Input hidden />
                      </Form.Item>
                      <Form.Item
                        style={{ marginRight: 25 }}
                        {...restField}
                        name={[name, 'orderProductId']}
                        label={t('PRODUCT')}
                        rules={[{ required: true, message: t('MISSING_PRODUCT') }]}
                      >
                        <Select style={{ width: 250 }}
                          onChange={() => { console.log(name); calcProps(name); }}>
                          {refundableProducts?.filter(op => ['refunded', 'partial_refund'].includes(op.status || ''))
                            .map((item: any) => {
                              return <Select.Option key={item.id} value={item.id}>
                                {item.friendly_name}
                              </Select.Option>;
                            })}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        noStyle
                        shouldUpdate
                      >
                        {() => {
                          let maxValue = 0;
                          const selected: any = refundableProducts?.find((item: any) => {
                            const prod = form.getFieldValue(['products', name, 'orderProductId']);
                            if (item.id === prod) return item;
                          });
                          maxValue = selected?.quantity;
                          return <Form.Item
                            style={{ marginRight: 25 }}
                            {...restField}
                            label={t('QUANTITY')}
                            name={[name, 'quantity']}
                            rules={[{ required: true, message: t('MISSING_QUANTITY') }]}
                          >
                            <InputNumber max={maxValue} min={0}
                              style={{ width: 70 }}
                              onChange={() => calcProps(name)} />
                          </Form.Item>;
                        }}
                      </Form.Item>
                      <Form.Item
                        style={{ marginRight: 30 }}
                        {...restField}
                        label={t('SUBTOTAL')}
                        name={[name, 'total']}
                      >
                        {form.getFieldValue(['products', name, 'total']) || 0} {currencySymbol}
                      </Form.Item>
                      <Form.Item
                        style={{ marginRight: 25 }}
                        {...restField}
                        label={t('VAT')}
                        name={[name, 'vat']}>
                        {form.getFieldValue(['products', name, 'vat']) || 0} {currencySymbol}
                      </Form.Item>
                      <Form.Item
                        style={{ marginRight: 25 }}
                        {...restField}
                        label={t('TOTAL')}>
                        {(form.getFieldValue(['products', name, 'vat']) || 0) +
                          (form.getFieldValue(['products', name, 'total']) || 0)}
                        {currencySymbol}
                      </Form.Item>
                      <MinusCircleOutlined style={{ color: 'red', marginLeft: 30 }} onClick={() => {
                        setCount(count - 1); remove(name);
                      }} />
                    </Space>
                  );
                })}
                <Form.Item>
                  <Button type="dashed" onClick={() => { setCount(count + 1); add(); }} block icon={<PlusOutlined />}>
                    {t('ADD_PRODUCT')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item>
            <Button
              loading={loading}
              type='primary'
              htmlType='submit'
            >
              {t('SAVE')}
            </Button>
          </Form.Item>
        </>}
        {!refundableProducts?.length && <div style={{ textAlign: 'center' }}>
          {t('NO_REFUNDABLE_PRODUCT_IN_ORDER')}
        </div>}
      </Form>
    </div>
  </Container >;
};


export default withTranslation()(CreditNoteForm);